"use client";

import React from "react";

import dynamic from "next/dynamic";
import Link from "@/components/link";
import { deleteCookie } from "cookies-next/client";

import { useAtom, useSetAtom } from "jotai";
import { ChevronDown, Menu } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import LoginButton from "@/components/login-button";

import {
  authActionAtom,
  authUserAtom,
  isAuthenticatedAtom,
} from "@/store/auth";

import { getUserProfile } from "@/fetchers/auth";

import useMediaQuery from "@/hooks/use-media-query";
import useMoengageEventTracker from "@/hooks/use-moengage";

import EVENT_NAME from "@/hooks/use-moengage/event-name.json";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute.json";

const BookIcon = dynamic(() => import("./icons/book.svg"));
const FeedIcon = dynamic(() => import("./icons/feed.svg"));
const IdeaIcon = dynamic(() => import("./icons/idea.svg"));
const BillIcon = dynamic(() => import("./icons/bill.svg"));
const ExamIcon = dynamic(() => import("./icons/exams.svg"));
const StoreIcon = dynamic(() => import("./icons/store.svg"));
const RobotIcon = dynamic(() => import("./icons/robot.svg"));
const LogoutIcon = dynamic(() => import("./icons/logout.svg"));
const BookmarkIcon = dynamic(() => import("./icons/bookmark.svg"));
const DeadlineIcon = dynamic(() => import("./icons/deadline.svg"));
const ComputerIcon = dynamic(() => import("./icons/computer.svg"));
const LiveClassIcon = dynamic(() => import("./icons/live-class.svg"));

function UserMenu() {
  const trackMoengageEvent = useMoengageEventTracker();
  const isTablet = useMediaQuery("(max-width: 768px)");

  const setAuthAction = useSetAtom(authActionAtom);

  const [isAuthenticated, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
  const [authUser, setAuthUser] = useAtom(authUserAtom);

  React.useEffect(() => {
    if (isAuthenticated && authUser) return;

    getUserProfile()
      .then((user) => {
        if (user) {
          setIsAuthenticated(true);
          setAuthUser(user);
          setAuthAction(1);
        } else {
          setIsAuthenticated(false);
        }
      })
      .catch(() => {
        setIsAuthenticated(false);

        if ("Moengage" in window) {
          Moengage.destroy_session();
        }
      });
  }, [
    isAuthenticated,
    setIsAuthenticated,
    setAuthUser,
    setAuthAction,
    authUser,
  ]);

  React.useEffect(() => {
    if (!isAuthenticated || !authUser) return;

    if ("Moengage" in window) {
      Moengage.add_user_name(`${authUser.firstName} ${authUser.lastName}`);
      Moengage.add_first_name(authUser.firstName);
      Moengage.add_email(authUser.email);
      Moengage.add_mobile(authUser.mobileNumber);
      Moengage.add_unique_user_id(authUser.userId || authUser.id);
      Moengage.update_unique_user_id(authUser.userId || authUser.id);
    }
  }, [isAuthenticated, authUser]);

  // If user not authenticated then render login button
  if (!isAuthenticated) return <LoginButton />;

  const handleOnItemClick = (type) => () => {
    let eventName = null;
    const eventAttributes = {
      [EVENT_ATTRIBUTE.SOURCE_SCREEN]: "header",
    };

    switch ((type || "").toLowerCase()) {
      case "library":
        eventName = EVENT_NAME.MY_LIBRARY_CLICKED;

        eventAttributes[EVENT_ATTRIBUTE.POSITION] = "myAccount";
        break;
      case "profile":
        eventName = EVENT_NAME.ACCOUNT_INFO_TAPPED;
        break;
      case "studyiq-ai":
        eventName = EVENT_NAME.NCERT_SUMMARY_TAPPED;

        eventAttributes[EVENT_ATTRIBUTE.POSITION] = "top";
        break;
      case "feed":
        eventName = EVENT_NAME.FEED_CLICKED;

        eventAttributes[EVENT_ATTRIBUTE.POSITION] = "top";
        eventAttributes[EVENT_ATTRIBUTE.BUTTON_SOURCE] = "myAccountDropDown";
        eventAttributes[EVENT_ATTRIBUTE.FEED_LANGUAGE] = "english";
        break;
      case "time-based-test":
        eventName = EVENT_NAME.FMT_CLICKED;
        break;
      case "daily-quizzes":
        eventName = EVENT_NAME.FREE_QUIZZES_TAPPED;
        break;
      case "free-courses":
        eventName = EVENT_NAME.FREE_COURSES_TAPPED;

        eventAttributes[EVENT_ATTRIBUTE.POSITION] = "top";
        break;
      default:
        eventName = null;
    }

    if (eventName) {
      trackMoengageEvent(eventName, eventAttributes);
    }
  };

  const handleOnLogoutClick = () => {
    if (typeof Moengage !== "undefined") {
      Moengage.destroy_session();
    }

    deleteCookie("userID");
    deleteCookie("apiToken");
    deleteCookie("deviceID");
    deleteCookie("mobileNumber");

    window.location.reload();
  };

  if (isTablet) {
    return (
      <Popover.Root modal>
        <Popover.Trigger asChild>
          <Button
            type="button"
            variant="outline"
            className="bg-white p-2.5 md:pl-5 h-10 rounded-lg border-primary/60 z-50"
          >
            <span className="text-sm hidden md:inline">My Account</span>
            <ChevronDown
              className="size-8! text-primary hidden md:inline-block"
              size={24}
            />

            <Menu className="size-8 md:hidden" />
          </Button>
        </Popover.Trigger>

        <Popover.Portal>
          <div className="fixed inset-0 z-50 [&>div]:transform-none! [&>div]:w-screen">
            <div className="fixed inset-0 bg-black opacity-40 z-30" />

            <Popover.Content
              className="flex flex-col h-dvh w-3/4 ml-auto border border-primary/60 bg-white rounded-md py-3 z-50"
              align="end"
              forceMount
            >
              <div className="flex gap-x-3 p-2.5">
                <Avatar className="size-17 bg-primary/25 border-2 border-primary">
                  <AvatarImage
                    src={authUser?.profileImage}
                    alt={authUser?.firstName}
                  />
                  <AvatarFallback className="text-white font-semibold text-3xl">
                    {authUser?.firstName?.[0].toUpperCase() || "S"}
                  </AvatarFallback>
                </Avatar>

                <div className="flex flex-col">
                  <span className="text-sm font-extrabold">
                    Hi, {authUser?.firstName.trim()}
                  </span>
                  <span className="text-sm font-normal">
                    Welcome to the StudyIQ
                  </span>
                  <Link
                    className="block text-sm font-extrabold text-primary underline mt-2"
                    href="/profile"
                  >
                    View Profile
                  </Link>
                </div>
              </div>

              <div className="p-2.5 mt-4">
                <ul className="space-y-4 text-grayscale-label/80 [&_a]:flex [&_a]:gap-x-3 [&_a]:py-1.25 [&_a]:px-2.5 [&_a]:text-sm [&_a]:leading-none [&_a]:hover:text-gray-700">
                  <li>
                    <Link href="/goal" onClick={handleOnItemClick("store")}>
                      <StoreIcon />
                      <span>Store</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/my-purchases"
                      onClick={handleOnItemClick("library")}
                    >
                      <BookIcon />
                      <span>My Purchases</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/bookmarks"
                      onClick={handleOnItemClick("bookmarks")}
                    >
                      <BookmarkIcon />
                      <span>Bookmarks</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/feeds/all/english"
                      onClick={handleOnItemClick("feed")}
                    >
                      <FeedIcon />
                      <span>Feed</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/dailyquizzes"
                      onClick={handleOnItemClick("daily-quizzes")}
                    >
                      <IdeaIcon />
                      <span>Daily Quizzes</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/my-mock-test"
                      onClick={handleOnItemClick("time-based-test")}
                    >
                      <DeadlineIcon />
                      <span>Time Based Test</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/free-courses"
                      onClick={handleOnItemClick("free-courses")}
                    >
                      <ComputerIcon />
                      <span>Free Courses</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/studyiq-ai"
                      onClick={handleOnItemClick("studyiq-ai")}
                    >
                      <RobotIcon />
                      <span>StudyIQ AI</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/free-live-classes"
                      onClick={handleOnItemClick("free-live-classes")}
                    >
                      <LiveClassIcon />
                      <span>Free Live Classes</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/profile/exam"
                      onClick={handleOnItemClick("my-exam")}
                    >
                      <ExamIcon />
                      <span>My Exams</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/profile/orders"
                      onClick={handleOnItemClick("my-transactions")}
                    >
                      <BillIcon />
                      <span>My Transactions</span>
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col gap-y-3 p-2.5 mt-auto mb-4 border-t border-gray-300">
                <ul className="space-y-2.5 text-grayscale-label/80">
                  <li>
                    <button
                      type="button"
                      className="flex gap-x-3 py-1.25 px-2.5 text-sm leading-none hover:text-[#9e0038] outline-none!"
                      onClick={handleOnLogoutClick}
                    >
                      <LogoutIcon />
                      <span>Logout</span>
                    </button>
                  </li>
                </ul>
              </div>
            </Popover.Content>
          </div>
        </Popover.Portal>
      </Popover.Root>
    );
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="outline"
          className="bg-white p-2.5 md:pl-5 h-10 rounded-lg border-primary/60 z-50"
        >
          <span className="text-sm hidden md:inline">My Account</span>
          <ChevronDown
            className="size-8! text-primary hidden md:inline-block"
            size={24}
          />

          <Menu className="size-8 md:hidden" />
        </Button>
      </Popover.Trigger>

      <Popover.Portal>
        <div className="relative">
          <div className="fixed inset-0 bg-black opacity-40 z-30" />

          <Popover.Content
            className="flex flex-col w-screen md:w-80 border border-primary/60 bg-white rounded-md py-3 z-50"
            align="end"
            sideOffset={8}
            forceMount
          >
            <div className="flex gap-x-3 p-2.5">
              <Avatar className="size-17 bg-primary/25 border-2 border-primary">
                <AvatarImage
                  src={authUser?.profileImage}
                  alt={authUser?.firstName}
                />
                <AvatarFallback className="text-white font-semibold text-3xl">
                  {authUser?.firstName?.[0].toUpperCase() || "S"}
                </AvatarFallback>
              </Avatar>

              <div className="flex flex-col">
                <span className="text-sm font-extrabold">
                  Hi, {authUser?.firstName.trim()}
                </span>
                <span className="text-sm font-normal">
                  Welcome to the StudyIQ
                </span>
                <Link
                  className="block text-sm font-extrabold text-primary underline mt-2"
                  href="/profile"
                >
                  View Profile
                </Link>
              </div>
            </div>

            <div className="p-2.5 pt-0 border-b border-gray-300">
              <ul className="space-y-2.5 text-grayscale-label/80 [&_a]:flex [&_a]:gap-x-3 [&_a]:py-1.25 [&_a]:px-2.5 [&_a]:text-sm [&_a]:leading-none [&_a]:hover:text-gray-700">
                <li>
                  <Link href="/goal" onClick={handleOnItemClick("store")}>
                    <StoreIcon />
                    <span>Store</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/my-purchases"
                    onClick={handleOnItemClick("library")}
                  >
                    <BookIcon />
                    <span>My Purchases</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/bookmarks"
                    onClick={handleOnItemClick("bookmarks")}
                  >
                    <BookmarkIcon />
                    <span>Bookmarks</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/feeds/all/english"
                    onClick={handleOnItemClick("feed")}
                  >
                    <FeedIcon />
                    <span>Feed</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/dailyquizzes"
                    onClick={handleOnItemClick("daily-quizzes")}
                  >
                    <IdeaIcon />
                    <span>Daily Quizzes</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/my-mock-test"
                    onClick={handleOnItemClick("time-based-test")}
                  >
                    <DeadlineIcon />
                    <span>Time Based Test</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/free-courses"
                    onClick={handleOnItemClick("free-courses")}
                  >
                    <ComputerIcon />
                    <span>Free Courses</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/studyiq-ai"
                    onClick={handleOnItemClick("studyiq-ai")}
                  >
                    <RobotIcon />
                    <span>StudyIQ AI</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/free-live-classes"
                    onClick={handleOnItemClick("free-live-classes")}
                  >
                    <LiveClassIcon />
                    <span>Free Live Classes</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/profile/exam"
                    onClick={handleOnItemClick("my-exam")}
                  >
                    <ExamIcon />
                    <span>My Exams</span>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/profile/orders"
                    onClick={handleOnItemClick("my-transactions")}
                  >
                    <BillIcon />
                    <span>My Transactions</span>
                  </Link>
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-y-3 p-2.5">
              <ul className="space-y-2.5 text-grayscale-label/80">
                <li>
                  <button
                    type="button"
                    className="flex gap-x-3 py-1.25 px-2.5 text-sm leading-none hover:text-[#9e0038] outline-none!"
                    onClick={handleOnLogoutClick}
                  >
                    <LogoutIcon />
                    <span>Logout</span>
                  </button>
                </li>
              </ul>

              <div className="px-2.5 [&_a]:before:inline-block [&_a]:before:px-1.75 [&_a]:before:content-['●'] [&_a]:before:text-grayscale-body [&_a]:first:before:hidden">
                <div className="flex [&>a]:text-xs text-grayscale-label/80">
                  <Link
                    href="/terms-and-conditions"
                    onClick={handleOnItemClick("terms")}
                  >
                    Terms & Conditions
                  </Link>
                  <Link
                    href="/privacy-policy"
                    onClick={handleOnItemClick("privacy")}
                  >
                    Privacy Policy
                  </Link>
                  <Link href="/about-us" onClick={handleOnItemClick("about")}>
                    About
                  </Link>
                </div>

                <div className="flex [&>a]:text-xs text-grayscale-label/80">
                  <Link href="/career" onClick={handleOnItemClick("career")}>
                    Careers
                  </Link>
                  <Link
                    href="https://www.studyiq.com/articles/"
                    onClick={handleOnItemClick("blog")}
                  >
                    Blog
                  </Link>
                  <Link href="/feedback" onClick={handleOnItemClick("support")}>
                    Support
                  </Link>
                </div>
              </div>
            </div>
          </Popover.Content>
        </div>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default UserMenu;
